import moment from "moment";
import {
    EMPTY_DRAFT,
    PlanDraftMilestone,
    PlanDraftOperation,
    PlanDraftPush,
    PlanDraftRange,
    PlanDraftTerm,
    PlanDraftView,
    PlanDraftWork,
    PlanOperationInput,
} from "./plan-draft";
import { PlanLinkType } from "./plan-dependencies";
import { ProjectPlanResponse } from "./project-plan-builder";

/**
 * Предпросмотр черновика прямо на странице — без запроса на каждое перетаскивание.
 *
 * Правила те же, что у PlanDraftEngine на сервере (он и считает при применении), только
 * в координатах колонок сетки: колонки — рабочие дни, поэтому сдвиг на N колонок и есть
 * сдвиг на N рабочих дней. Прошлое (колонки раньше сегодняшней) не двигается, «с даты»
 * двигает хвост, чужие работы не расталкиваются. После операций — каскад по связям
 * (cascade): зависимые толкаются вперёд, назад не тянутся. Работы видны только в
 * загруженном диапазоне — за его краями предпросмотр их не знает, это решает сервер.
 */

const API_DAY = "YYYY-MM-DD";

interface Work {
    id: string;
    employerId: string;
    orderId: string;
    contentGuid: string | null;
    /** Колонки дней работы по возрастанию */
    days: number[];
}

interface Term {
    orderId: string;
    contentGuid: string | null;
    start: number | null;
    end: number | null;
}

interface Milestone {
    id: string;
    orderId: string;
    column: number;
}

interface LinkEnd {
    kind: "work" | "section";
    orderId: string;
    assignmentId: string | null;
    contentGuid: string | null;
}

interface Link {
    id: string;
    type: PlanLinkType;
    lag: number;
    from: LinkEnd;
    to: LinkEnd;
}

interface State {
    works: Map<string, Work>;
    milestones: Map<string, Milestone>;
    terms: Term[];
}

/** Итог каскада: толчки по ключу конца */
interface CascadeResult {
    pushed: Map<string, { end: LinkEnd; shift: number }>;
    blocked: Map<string, { end: LinkEnd; shift: number }>;
}

export interface LocalDraft {
    view: PlanDraftView;
    /** Причина отказа по номеру операции */
    rejected: Map<number, string>;
}

/** Рабочие дни загруженного диапазона — как колонки сетки */
export function planDays(response: ProjectPlanResponse): string[] {
    return (response.periods || []).flatMap((period) =>
        (period.days || []).filter((day) => !day.isDayOff).map((day) => day.day)
    );
}

export function computeDraft(
    response: ProjectPlanResponse | null,
    operations: PlanOperationInput[],
    today: string = moment().format(API_DAY)
): LocalDraft {
    if (!response || !operations.length) {
        return { view: EMPTY_DRAFT, rejected: new Map() };
    }

    const days = planDays(response);
    const cut = firstFuture(days, today);
    const model = new Calendar(days);

    const baseline = readState(response, model);
    let state = clone(baseline);
    const rejected = new Map<number, string>();

    operations.forEach((operation, index) => {
        const attempt = clone(state);
        const error = applyOne(attempt, operation, model, cut);

        if (error) {
            rejected.set(index, error);
        } else {
            state = attempt;
        }
    });

    const cascaded = cascade(baseline, state, readLinks(response), cut, canPushIn(response));

    return { view: toView(baseline, state, operations, rejected, model, cascaded), rejected };
}

/** Колонки ↔ даты. За краями диапазона — по будням: календаря там нет */
class Calendar {
    constructor(readonly days: string[]) {}

    column(day: string): number {
        let low = 0;
        let high = this.days.length;

        while (low < high) {
            const middle = (low + high) >> 1;

            if (this.days[middle] < day) {
                low = middle + 1;
            } else {
                high = middle;
            }
        }

        return low;
    }

    day(column: number): string {
        if (column >= 0 && column < this.days.length) {
            return this.days[column];
        }

        if (!this.days.length) {
            return "";
        }

        const forward = column >= this.days.length;
        let date = moment(forward ? this.days[this.days.length - 1] : this.days[0], API_DAY);
        let steps = forward ? column - (this.days.length - 1) : -column;

        while (steps > 0) {
            date = date.add(forward ? 1 : -1, "day");

            if (date.isoWeekday() < 6) {
                steps--;
            }
        }

        return date.format(API_DAY);
    }
}

function firstFuture(days: string[], today: string): number {
    const index = days.findIndex((day) => day >= today);
    return index < 0 ? days.length : index;
}

function readState(response: ProjectPlanResponse, calendar: Calendar): State {
    const works = new Map<string, Work>();
    const milestones = new Map<string, Milestone>();
    const terms: Term[] = [];

    (response.orders || []).forEach((order) => {
        // раздел работы — из режима «Разделы», там строки разложены по разделам
        const sectionOf = new Map<string, string>();

        (order.sections || []).forEach((section) =>
            (section.rows || []).forEach((row) =>
                (row.segments || []).forEach((segment) => {
                    if (segment.assignmentId && section.id) {
                        sectionOf.set(segment.assignmentId, section.id);
                    }
                })
            )
        );

        (order.rows || []).forEach((row) => {
            if (row.kind !== "employer") {
                return;
            }

            (row.segments || []).forEach((segment) => {
                if (!segment.assignmentId) {
                    return;
                }

                let work = works.get(segment.assignmentId);

                if (!work) {
                    work = {
                        id: segment.assignmentId,
                        employerId: row.id,
                        orderId: order.id,
                        contentGuid: sectionOf.get(segment.assignmentId) || null,
                        days: [],
                    };
                    works.set(work.id, work);
                }

                const from = calendar.column(segment.from);
                const to = calendar.column(segment.to);

                for (let column = from; column <= to && column < calendar.days.length; column++) {
                    work.days.push(column);
                }
            });
        });

        (order.milestones || []).forEach((milestone) =>
            milestones.set(milestone.id, {
                id: milestone.id,
                orderId: order.id,
                column: calendar.column(milestone.day),
            })
        );

        (order.terms || []).forEach((term) =>
            terms.push({
                orderId: order.id,
                contentGuid: term.contentGuid || null,
                start: term.start ? calendar.column(term.start) : null,
                end: term.end ? calendar.column(term.end) : null,
            })
        );
    });

    works.forEach((work) => {
        work.days = Array.from(new Set(work.days)).sort((left, right) => left - right);
    });

    return { works, milestones, terms };
}

function clone(state: State): State {
    const works = new Map<string, Work>();
    state.works.forEach((work, id) => works.set(id, { ...work, days: work.days.slice() }));

    const milestones = new Map<string, Milestone>();
    state.milestones.forEach((milestone, id) => milestones.set(id, { ...milestone }));

    return { works, milestones, terms: state.terms.map((term) => ({ ...term })) };
}

function applyOne(state: State, operation: PlanOperationInput, calendar: Calendar, cut: number): string | null {
    const shift = operation.shift || 0;
    const guid = operation.contentGuid || null;

    switch (operation.kind) {
        case "work": {
            const work = operation.assignmentId ? state.works.get(operation.assignmentId) : undefined;

            if (!work) {
                return "Работа не найдена";
            }

            if (work.days.every((day) => day < cut)) {
                return "Работа уже в прошлом — сдвигать нечего";
            }

            return shiftWorks([work], shift, cut, cut);
        }

        case "work-end":
        case "work-start": {
            const work = operation.assignmentId ? state.works.get(operation.assignmentId) : undefined;

            if (!work) {
                return "Работа не найдена";
            }

            // fromDay — якорь: край отрезка, за который тянут; без него — край всей работы
            if (operation.fromDay) {
                const anchor = calendar.column(operation.fromDay);

                return operation.kind === "work-end"
                    ? moveEndAt(work, shift, anchor, cut)
                    : moveStartAt(work, shift, anchor, cut);
            }

            return operation.kind === "work-end" ? moveEnd(work, shift, cut) : moveStart(work, shift, cut);
        }

        case "section": {
            const from = limit(operation.fromDay, calendar, cut);
            const works = Array.from(state.works.values()).filter(
                (work) => work.orderId === operation.orderId && work.contentGuid === guid
            );
            const term = guid ? findTerm(state, operation.orderId, guid) : undefined;

            if (!works.length && !term) {
                return "В разделе нет работ";
            }

            return shiftWorks(works, shift, from, cut) || shiftTerm(term, shift, from, cut);
        }

        case "project": {
            const from = limit(operation.fromDay, calendar, cut);
            const works = Array.from(state.works.values()).filter((work) => work.orderId === operation.orderId);

            // двигать нечего (например, в проекте только подрядчик) — вехи и сроки
            // разделов в одиночку не уезжают
            if (!works.some((work) => work.days.some((day) => day >= from))) {
                return "В проекте нет работ, которые можно сдвинуть";
            }

            const error = shiftWorks(works, shift, from, cut);

            if (error) {
                return error;
            }

            state.milestones.forEach((milestone) => {
                if (milestone.orderId === operation.orderId && milestone.column >= from) {
                    milestone.column += shift;
                }
            });

            // сроки разделов едут с проектом, плановый срок проекта — нет: его меняют только краем
            for (const term of state.terms.filter((item) => item.orderId === operation.orderId && !!item.contentGuid)) {
                const termError = shiftTerm(term, shift, from, cut);

                if (termError) {
                    return termError;
                }
            }

            return null;
        }

        case "milestone": {
            const milestone = operation.milestoneId ? state.milestones.get(operation.milestoneId) : undefined;

            if (!milestone) {
                return "Веха не найдена";
            }

            milestone.column += shift;
            return null;
        }

        case "section-start":
        case "section-end": {
            if (!guid) {
                return "У строки «Без раздела» срока нет";
            }

            if (!operation.day) {
                return "Не указана дата";
            }

            const column = calendar.column(operation.day);

            if (column < cut) {
                return "Срок нельзя перенести в прошлое";
            }

            let term = findTerm(state, operation.orderId, guid);

            if (!term) {
                const days = Array.from(state.works.values())
                    .filter((work) => work.orderId === operation.orderId && work.contentGuid === guid)
                    .flatMap((work) => work.days);

                term = {
                    orderId: operation.orderId || "",
                    contentGuid: guid,
                    start: days.length ? Math.min(...days) : null,
                    end: days.length ? Math.max(...days) : null,
                };
                state.terms.push(term);
            }

            if (operation.kind === "section-start") {
                term.start = column;
            } else {
                term.end = column;
            }

            return term.start !== null && term.end !== null && term.start > term.end
                ? "Начало срока позже окончания"
                : null;
        }

        case "project-end": {
            if (!operation.day) {
                return "Не указана дата";
            }

            const column = calendar.column(operation.day);

            if (column < cut) {
                return "Срок нельзя перенести в прошлое";
            }

            let term = findTerm(state, operation.orderId, null);

            if (!term) {
                term = { orderId: operation.orderId || "", contentGuid: null, start: null, end: null };
                state.terms.push(term);
            }

            term.end = column;
            return null;
        }

        default:
            return "Неизвестная операция";
    }
}

function shiftWorks(works: Work[], shift: number, from: number, cut: number): string | null {
    if (!shift) {
        return null;
    }

    for (const work of works) {
        const moved = work.days.map((day) => (day >= from ? day + shift : day));

        // сдвигаемые дни не могут уйти раньше сегодняшнего
        if (work.days.some((day, index) => day >= from && moved[index] < cut)) {
            return "Сдвиг уводит работу в прошлое";
        }

        work.days = Array.from(new Set(moved)).sort((left, right) => left - right);
    }

    return null;
}

function moveEnd(work: Work, shift: number, cut: number): string | null {
    if (!shift || !work.days.length) {
        return null;
    }

    if (shift > 0) {
        const last = work.days[work.days.length - 1];

        for (let day = last + 1; day <= last + shift; day++) {
            if (day >= cut) {
                work.days.push(day);
            }
        }

        return null;
    }

    const removable = work.days.filter((day) => day >= cut).length;

    if (-shift > removable) {
        return "Прошедшие дни не снимаются";
    }

    if (-shift >= work.days.length) {
        return "В работе должен остаться хотя бы один день";
    }

    work.days = work.days.slice(0, work.days.length + shift);
    return null;
}

function moveStart(work: Work, shift: number, cut: number): string | null {
    if (!shift || !work.days.length) {
        return null;
    }

    if (shift < 0) {
        const first = work.days[0];

        if (first + shift < cut) {
            return "Работу нельзя начать в прошлом";
        }

        const added: number[] = [];

        for (let day = first + shift; day < first; day++) {
            added.push(day);
        }

        work.days = [...added, ...work.days];
        return null;
    }

    if (shift >= work.days.length) {
        return "В работе должен остаться хотя бы один день";
    }

    if (work.days.slice(0, shift).some((day) => day < cut)) {
        return "Прошедшие дни не снимаются";
    }

    work.days = work.days.slice(shift);
    return null;
}

/**
 * Левый край отрезка, начинающегося в колонке anchor: минус — дописать дни перед ним
 * (в дыру или раньше начала), плюс — снять первые дни отрезка. Прошлое не трогается.
 * Те же правила, что PlanDraftEngine.MoveStartAt
 */
function moveStartAt(work: Work, shift: number, anchor: number, cut: number): string | null {
    if (!shift || !work.days.length) {
        return null;
    }

    if (shift < 0) {
        const added: number[] = [];

        for (let day = anchor - 1; day >= anchor + shift; day--) {
            if (day < cut) {
                return "Работу нельзя начать в прошлом";
            }

            added.push(day);
        }

        work.days = Array.from(new Set([...work.days, ...added])).sort((left, right) => left - right);
        return null;
    }

    return removeDays(work, work.days.filter((day) => day >= anchor).slice(0, shift), cut);
}

/** Правый край отрезка, кончающегося в колонке anchor: плюс — дописать дни после, минус — снять последние */
function moveEndAt(work: Work, shift: number, anchor: number, cut: number): string | null {
    if (!shift || !work.days.length) {
        return null;
    }

    if (shift > 0) {
        const added: number[] = [];

        for (let day = anchor + 1; day <= anchor + shift; day++) {
            if (day >= cut) {
                added.push(day);
            }
        }

        work.days = Array.from(new Set([...work.days, ...added])).sort((left, right) => left - right);
        return null;
    }

    return removeDays(
        work,
        work.days
            .filter((day) => day <= anchor)
            .reverse()
            .slice(0, -shift),
        cut
    );
}

/** Снять дни работы: прошедшие не снимаются, хотя бы один день остаётся */
function removeDays(work: Work, days: number[], cut: number): string | null {
    if (days.some((day) => day < cut)) {
        return "Прошедшие дни не снимаются";
    }

    if (days.length >= work.days.length) {
        return "В работе должен остаться хотя бы один день";
    }

    const removed = new Set(days);
    work.days = work.days.filter((day) => !removed.has(day));
    return null;
}

function shiftTerm(term: Term | undefined, shift: number, from: number, cut: number): string | null {
    if (!term || !shift) {
        return null;
    }

    if (term.start !== null && term.start >= from) {
        term.start += shift;
    }

    if (term.end !== null && term.end >= from) {
        term.end += shift;

        if (term.end < cut) {
            return "Сдвиг уводит срок в прошлое";
        }
    }

    return null;
}

function findTerm(state: State, orderId: string | null | undefined, guid: string | null): Term | undefined {
    return state.terms.find((term) => term.orderId === orderId && term.contentGuid === guid);
}

/** «Сдвинуть с даты»: не раньше сегодняшнего дня */
function limit(fromDay: string | null | undefined, calendar: Calendar, cut: number): number {
    return fromDay ? Math.max(cut, calendar.column(fromDay)) : cut;
}

/* ---------- каскад по связям (этап 6.3) — те же правила, что PlanDraftEngine.Cascade ---------- */

function readLinks(response: ProjectPlanResponse): Link[] {
    return (response.links || [])
        .filter((link) => !!link.from && !!link.to)
        .map((link) => ({
            id: link.id,
            type: link.type,
            lag: link.lag || 0,
            from: toEnd(link.from!),
            to: toEnd(link.to!),
        }));
}

function toEnd(end: {
    kind: "work" | "section";
    orderId: string;
    assignmentId: string | null;
    contentGuid: string | null;
}): LinkEnd {
    return {
        kind: end.kind,
        orderId: end.orderId,
        assignmentId: end.assignmentId || null,
        contentGuid: end.contentGuid || null,
    };
}

const endKeyOf = (end: LinkEnd) =>
    end.kind === "work" ? `work:${end.assignmentId}` : `section:${end.orderId}/${end.contentGuid}`;

/**
 * Права каскада — те же, что на операцию: работа — все проекты, ГИП проекта или начальник
 * отдела сотрудника; раздел — все проекты или ГИП. Чужое не толкается
 */
function canPushIn(response: ProjectPlanResponse): (end: LinkEnd, state: State) => boolean {
    if (response.editAll) {
        return () => true;
    }

    const gip = new Set((response.orders || []).filter((order) => order.canEditMilestones).map((order) => order.id));
    const employee = new Set(response.editableEmployers || []);

    return (end, state) => {
        if (gip.has(end.orderId)) {
            return true;
        }

        if (end.kind !== "work" || !end.assignmentId) {
            return false;
        }

        const work = state.works.get(end.assignmentId);
        return !!work && employee.has(work.employerId);
    };
}

/**
 * Последователь, которому правка сдвинула ограничение позже, толкается вперёд целиком на
 * недостающие рабочие дни — и дальше по цепочке. Назад не тянем; нарушение, бывшее до
 * черновика, не лечим; край последователя в прошлом не толкаем
 */
function cascade(
    baseline: State,
    state: State,
    links: Link[],
    cut: number,
    canPush: (end: LinkEnd, state: State) => boolean
): CascadeResult {
    const result: CascadeResult = { pushed: new Map(), blocked: new Map() };

    if (!links.length) {
        return result;
    }

    for (let pass = 0; pass <= links.length; pass++) {
        let changed = false;

        links.forEach((link) => {
            const shift = needed(baseline, state, link, cut);

            if (shift <= 0) {
                return;
            }

            if (!canPush(link.to, state)) {
                result.blocked.set(link.id, { end: link.to, shift });
                return;
            }

            pushEnd(state, link.to, shift, cut);

            const key = endKeyOf(link.to);
            const push = result.pushed.get(key) || { end: link.to, shift: 0 };
            push.shift += shift;
            result.pushed.set(key, push);
            changed = true;
        });

        if (!changed) {
            break;
        }
    }

    return result;
}

function needed(baseline: State, state: State, link: Link, cut: number): number {
    const from = place(state, link.from, false, cut);
    const fromBefore = place(baseline, link.from, false, cut);
    const to = place(state, link.to, true, cut);
    const toBefore = place(baseline, link.to, true, cut);

    if (!from || !fromBefore || !to || !toBefore) {
        return 0;
    }

    const required = requiredOf(link, from);
    const requiredBefore = requiredOf(link, fromBefore);

    // предшественник не ушёл позже — связь правкой не задета
    if (required <= requiredBefore) {
        return 0;
    }

    const byStart = link.type === "fs" || link.type === "ss";
    const actual = byStart ? to.start : to.end;

    if (actual < cut) {
        return 0;
    }

    const actualBefore = byStart ? toBefore.start : toBefore.end;
    // нарушение до черновика сохраняется как было — толкаем только добавленное правкой
    const debt = Math.max(0, requiredBefore - actualBefore);

    return Math.max(0, required - debt - actual);
}

/** Колонки — рабочие дни, поэтому сдвиг на N рабочих дней — это +N */
function requiredOf(link: Link, from: { start: number; end: number }): number {
    const edge = link.type === "fs" || link.type === "ff" ? from.end : from.start;
    return edge + (link.type === "fs" ? 1 + link.lag : link.lag);
}

/** Где конец: работа с дырами — кусок (последователь — первый живой, предшественник — последний) */
function place(state: State, end: LinkEnd, successor: boolean, cut: number): { start: number; end: number } | null {
    if (end.kind === "work") {
        const work = end.assignmentId ? state.works.get(end.assignmentId) : undefined;

        if (!work || !work.days.length) {
            return null;
        }

        const pieces: number[][] = [];

        work.days.forEach((day, index) => {
            if (index === 0 || work.days[index - 1] + 1 < day) {
                pieces.push([]);
            }

            pieces[pieces.length - 1].push(day);
        });

        const alive = pieces.filter((piece) => piece[piece.length - 1] >= cut);
        const list = alive.length ? alive : pieces;
        const piece = successor ? list[0] : list[list.length - 1];

        return { start: piece[0], end: piece[piece.length - 1] };
    }

    const term = findTerm(state, end.orderId, end.contentGuid);

    if (term && term.start !== null && term.end !== null) {
        return { start: term.start, end: term.end };
    }

    const days = sectionWorks(state, end).flatMap((work) => work.days);

    return days.length ? { start: Math.min(...days), end: Math.max(...days) } : null;
}

function sectionWorks(state: State, end: LinkEnd): Work[] {
    return end.contentGuid
        ? Array.from(state.works.values()).filter(
              (work) => work.orderId === end.orderId && work.contentGuid === end.contentGuid
          )
        : [];
}

function pushEnd(state: State, end: LinkEnd, shift: number, cut: number) {
    if (end.kind === "work") {
        const work = end.assignmentId ? state.works.get(end.assignmentId) : undefined;

        if (work) {
            shiftWorks([work], shift, cut, cut);
        }

        return;
    }

    shiftWorks(sectionWorks(state, end), shift, cut, cut);
    shiftTerm(findTerm(state, end.orderId, end.contentGuid), shift, cut, cut);
}

/* ---------- состояние → то же представление, что отдаёт сервер ---------- */

function ranges(days: number[], calendar: Calendar): PlanDraftRange[] {
    const result: PlanDraftRange[] = [];

    days.forEach((day, index) => {
        const last = result[result.length - 1];

        if (last && index > 0 && days[index - 1] === day - 1) {
            last.to = calendar.day(day);
            last.workDays++;
        } else {
            result.push({ from: calendar.day(day), to: calendar.day(day), workDays: 1 });
        }
    });

    return result;
}

function toView(
    before: State,
    after: State,
    operations: PlanOperationInput[],
    rejected: Map<number, string>,
    calendar: Calendar,
    cascaded: CascadeResult
): PlanDraftView {
    const works: PlanDraftWork[] = [];

    const pushedOf = (work: Work) =>
        Array.from(cascaded.pushed.values())
            .filter(({ end }) =>
                end.kind === "work"
                    ? end.assignmentId === work.id
                    : end.orderId === work.orderId && !!work.contentGuid && end.contentGuid === work.contentGuid
            )
            .reduce((sum, push) => sum + push.shift, 0);

    after.works.forEach((work, id) => {
        const original = before.works.get(id);

        if (original && original.days.join() !== work.days.join()) {
            works.push({
                id,
                employerId: work.employerId,
                orderId: work.orderId,
                contentGuid: work.contentGuid,
                before: ranges(original.days, calendar),
                after: ranges(work.days, calendar),
                pushed: pushedOf(work),
            });
        }
    });

    const milestones: PlanDraftMilestone[] = [];

    after.milestones.forEach((milestone, id) => {
        const original = before.milestones.get(id);

        if (original && original.column !== milestone.column) {
            milestones.push({
                id,
                orderId: milestone.orderId,
                before: calendar.day(original.column),
                after: calendar.day(milestone.column),
            });
        }
    });

    const terms: PlanDraftTerm[] = [];

    after.terms.forEach((term) => {
        const original = before.terms.find(
            (item) => item.orderId === term.orderId && item.contentGuid === term.contentGuid
        );

        if (!original || original.start !== term.start || original.end !== term.end) {
            terms.push({
                orderId: term.orderId,
                contentGuid: term.contentGuid,
                beforeStart: original && original.start !== null ? calendar.day(original.start) : null,
                beforeEnd: original && original.end !== null ? calendar.day(original.end) : null,
                afterStart: term.start !== null ? calendar.day(term.start) : null,
                afterEnd: term.end !== null ? calendar.day(term.end) : null,
            });
        }
    });

    const orders = Array.from(
        new Set([...works.map((w) => w.orderId), ...milestones.map((m) => m.orderId), ...terms.map((t) => t.orderId)])
    );

    const pushView = (end: LinkEnd, shift: number, linkId: string | null): PlanDraftPush => {
        const work = end.kind === "work" && end.assignmentId ? before.works.get(end.assignmentId) : undefined;

        return {
            kind: end.kind,
            orderId: end.orderId,
            assignmentId: end.assignmentId,
            contentGuid: end.contentGuid,
            employerId: work ? work.employerId : null,
            shift,
            linkId,
        };
    };

    const pushed = Array.from(cascaded.pushed.values()).map((push) => pushView(push.end, push.shift, null));
    const blocked = Array.from(cascaded.blocked.entries()).map(([linkId, push]) =>
        pushView(push.end, push.shift, linkId)
    );

    // проекты, которых касаются сами операции, — остальное задел только каскад
    const own = new Set(
        operations
            .map((operation) => {
                if (operation.orderId) {
                    return operation.orderId;
                }

                const work = operation.assignmentId ? before.works.get(operation.assignmentId) : undefined;
                const milestone = operation.milestoneId ? before.milestones.get(operation.milestoneId) : undefined;

                return work ? work.orderId : milestone ? milestone.orderId : null;
            })
            .filter((id): id is string => !!id)
    );

    const cascadeOrders = Array.from(
        new Set([...pushed, ...blocked].map((push) => push.orderId).filter((id) => !!id && !own.has(id)))
    );

    return {
        active: true,
        started: null,
        operations: operations.map((operation, index): PlanDraftOperation => ({
            ...operation,
            index,
            rejected: rejected.get(index) || null,
        })),
        works,
        milestones,
        terms,
        changesCount: works.length + milestones.length + terms.length,
        orders,
        pushed,
        blocked,
        cascadeOrders,
    };
}
